import { Loader2, MoreHorizontal, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  billingLabelForSite,
  daysUntil,
  paletteForSite,
  provisioningMsLeft,
  siteDomain,
  siteName,
  statusLabel,
  statusToUi,
} from './adminUtils';
import { ADD_SITE_OFFER_COPY } from '../../helpers/hostedOffer';

// Live "still provisioning" countdown for a freshly created site. SSL and full
// setup usually finish in 2-4 minutes; this reassures the user it is in flight.
function ProvisioningTimer({ site }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const msLeft = provisioningMsLeft(site);
  if (msLeft <= 0) return null;
  const minutes = Math.floor(msLeft / 60000);
  const seconds = Math.floor((msLeft % 60000) / 1000);

  return (
    <div className="site-provisioning">
      <Loader2 size={13} className="spin" />
      <span>
        Provisioning - issuing SSL, ~{minutes}:{String(seconds).padStart(2, '0')} remaining
      </span>
    </div>
  );
}

export function StatusPill({ status }) {
  const uiStatus = statusToUi(status);
  return <span className={`admin-pill ${uiStatus}`}>{statusLabel(status)}</span>;
}

function MiniPage() {
  return (
    <div className="mini-page">
      <div className="mini-controls">
        <span />
        <span />
        <span />
      </div>
      <div className="mini-title" />
      <div className="mini-line" />
      <div className="mini-line short" />
    </div>
  );
}

export function AddSiteCard({ onClick, disabled = false, disabledReason }) {
  return (
    <button
      className="site-card add-site-card"
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? disabledReason : undefined}
    >
      <span className="add-icon">
        <Plus size={23} />
      </span>
      <span className="add-title">Add another site</span>
      <span className="add-copy">
        {disabled ? disabledReason : ADD_SITE_OFFER_COPY}
      </span>
    </button>
  );
}

export default function SiteCard({ site }) {
  const domain = siteDomain(site);
  const name = siteName(site);
  const uiStatus = statusToUi(site?.status);
  const deletionDays = site?.status === 'disabled' ? daysUntil(site?.delete_due_at) : null;

  return (
    <Link className="site-card" to={`/sites/${site.id}`}>
      <div className={`site-thumb ${paletteForSite(site)}`}>
        <MiniPage />
      </div>
      <div className="site-card-body">
        <div className="site-card-head">
          <div>
            <h3>{name}</h3>
            <p>{domain}</p>
          </div>
          <button className="site-menu-button" type="button" aria-label={`Open menu for ${name}`} onClick={(event) => event.preventDefault()}>
            <MoreHorizontal size={17} />
          </button>
        </div>
        <div className="site-meta-row">
          <StatusPill status={site?.status} />
          <span className="dot-separator">.</span>
          <span>{billingLabelForSite(site)}</span>
        </div>
        <div className="site-meta-row muted">
          <span>{uiStatus === 'live' ? 'Production site' : uiStatus === 'provisioning' ? 'Provisioning in progress' : 'Not yet billable'}</span>
          <span className="dot-separator">.</span>
          <span>{site?.created_at ? `Created ${new Date(site.created_at).toLocaleDateString()}` : 'Recently created'}</span>
        </div>
        <ProvisioningTimer site={site} />
        {deletionDays !== null ? (
          <div className="site-deletion-warning">
            {deletionDays} day{deletionDays === 1 ? '' : 's'} until deletion - resubscribe to keep
          </div>
        ) : null}
      </div>
    </Link>
  );
}
